import React from 'react';

interface ImagePropertiesProps {
  properties: {
    width?: string | number | null;
    height?: string | number | null;
    aspect_ratio?: string;
    resolution?: string;
    orientation?: string;
    format?: string;
    size_bytes?: string | number;
    // Video-specific properties
    duration_seconds?: number | null;
    frame_rate?: number | null;
    bitrate_kbps?: number | null;
    codec?: string | null;
  };
  /** Compact mode: renders all details under a single "Meta Details" card to save space (e.g. inside modals) */
  compact?: boolean;
}

const ImageProperties: React.FC<ImagePropertiesProps> = ({ properties, compact = false }) => {
  // Detect if this is a video based on presence of video-specific properties
  const isVideo = properties.duration_seconds !== undefined || 
                  properties.frame_rate !== undefined || 
                  properties.bitrate_kbps !== undefined || 
                  properties.codec !== undefined;

  // Parse width/height (remove any date-like suffixes)
  const parsePixels = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const match = value.toString().match(/^(\d+)/);
    return match ? `${match[1]}px` : value.toString();
  };

  // Parse duration
  const parseDuration = (seconds: number | null | undefined): string => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  // Parse frame rate
  const parseFrameRate = (fps: number | null | undefined): string => {
    if (!fps) return 'N/A';
    return `${fps} fps`;
  };

  // Parse bitrate
  const parseBitrate = (kbps: number | null | undefined): string => {
    if (!kbps) return 'N/A';
    if (kbps >= 1000) {
      return `${(kbps / 1000).toFixed(2)} Mbps`;
    }
    return `${kbps} kbps`;
  };

  // Parse file size
  const parseFileSize = (bytes: string | number | undefined): string => {
    if (!bytes) return 'N/A';
    
    // Convert to string if it's a number
    const bytesStr = typeof bytes === 'number' ? bytes.toString() : bytes;
    
    // Try to extract number from string
    const match = bytesStr.match(/^(\d+)/);
    if (!match) return bytesStr;
    
    const numBytes = parseInt(match[1]);
    if (numBytes < 1024) return `${numBytes} B`;
    if (numBytes < 1024 * 1024) return `${(numBytes / 1024).toFixed(2)} KB`;
    return `${(numBytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  if (compact) {
    return (
      <div
        className="p-4 rounded-lg border border-gray-700"
        style={{
          backdropFilter: 'blur(10px)',
          boxShadow: '0 0 14px 0 #0000001a',
          background: 'linear-gradient(90deg, rgba(33, 33, 33, 0.92), rgba(55, 55, 55, 0.92))',
          border: '1px solid rgba(224, 224, 224, 0.13)'
        }}
      >
        <h3 className="text-sm font-semibold text-white mb-3">Meta Details</h3>
        <div className="grid grid-cols-3 gap-3">
          <PropertyItem label="Width" value={parsePixels(properties.width)} />
          <PropertyItem label="Height" value={parsePixels(properties.height)} />
          <PropertyItem label="Aspect Ratio" value={properties.aspect_ratio || 'N/A'} />
          <PropertyItem label="Orientation" value={properties.orientation || 'vertical'} />
          {isVideo && (
            <PropertyItem label="Duration" value={parseDuration(properties.duration_seconds)} />
          )}
          <PropertyItem label="Resolution" value={properties.resolution || 'N/A'} />
          <PropertyItem label="Format" value={properties.format?.toUpperCase() || 'N/A'} />
          <PropertyItem label="File Size" value={parseFileSize(properties.size_bytes)} />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Dimensions Section */}
      <div 
        className="p-4 rounded-lg border border-gray-700"
        style={{
          backdropFilter: 'blur(10px)',
          boxShadow: '0 0 14px 0 #0000001a',
          background: 'linear-gradient(90deg, rgba(33, 33, 33, 0.92), rgba(55, 55, 55, 0.92))',
          border: '1px solid rgba(224, 224, 224, 0.13)'
        }}
      >
        <h3 className="text-sm font-semibold text-white mb-3">Dimensions</h3>
        <div className="grid grid-cols-2 gap-3">
          <PropertyItem label="Width" value={parsePixels(properties.width)} />
          <PropertyItem label="Height" value={parsePixels(properties.height)} />
          <PropertyItem label="Aspect Ratio" value={properties.aspect_ratio || 'N/A'} />
          <PropertyItem label="Orientation" value={properties.orientation || 'vertical'} />
        </div>
      </div>

      {/* Video Quality Section - Only for videos */}
      {isVideo && (
        <div 
          className="p-4 rounded-lg border border-gray-700"
          style={{
            backdropFilter: 'blur(10px)',
            boxShadow: '0 0 14px 0 #0000001a',
            background: 'linear-gradient(90deg, rgba(33, 33, 33, 0.92), rgba(55, 55, 55, 0.92))',
            border: '1px solid rgba(224, 224, 224, 0.13)'
          }}
        >
          <h3 className="text-sm font-semibold text-white mb-3">Video Quality</h3>
          <div className="grid grid-cols-2 gap-3">
            <PropertyItem label="Duration" value={parseDuration(properties.duration_seconds)} />
            {/* <PropertyItem label="Frame Rate" value={parseFrameRate(properties.frame_rate)} /> */}
            {/* <PropertyItem label="Bitrate" value={parseBitrate(properties.bitrate_kbps)} /> */}
            {/* <PropertyItem label="Codec" value={properties.codec?.toUpperCase() || 'N/A'} /> */}
          </div>
        </div>
      )}

      {/* Quality Section */}
      <div 
        className="p-4 rounded-lg border border-gray-700"
        style={{
          backdropFilter: 'blur(10px)',
          boxShadow: '0 0 14px 0 #0000001a',
          background: 'linear-gradient(90deg, rgba(33, 33, 33, 0.92), rgba(55, 55, 55, 0.92))',
          border: '1px solid rgba(224, 224, 224, 0.13)'
        }}
      >
        <h3 className="text-sm font-semibold text-white mb-3">Quality</h3>
        <div className="grid grid-cols-2 gap-3">
          <PropertyItem label="Resolution" value={properties.resolution || 'N/A'} />
          <PropertyItem label="Format" value={properties.format?.toUpperCase() || 'N/A'} />
        </div>
      </div>

      {/* Size Section */}
      <div 
        className="p-4 rounded-lg border border-gray-700"
        style={{
          backdropFilter: 'blur(10px)',
          boxShadow: '0 0 14px 0 #0000001a',
          background: 'linear-gradient(90deg, rgba(33, 33, 33, 0.92), rgba(55, 55, 55, 0.92))',
          border: '1px solid rgba(224, 224, 224, 0.13)'
        }}
      >
        <h3 className="text-sm font-semibold text-white mb-3">Size</h3>
        <div className="grid grid-cols-1 gap-3">
          <PropertyItem label="File Size" value={parseFileSize(properties.size_bytes)} />
        </div>
      </div>
    </div>
  );
};

const PropertyItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs text-gray-400 mb-1">{label}</span>
    <span className="text-sm text-white font-medium">{value}</span>
  </div>
);

export default ImageProperties;
